import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useRecent } from './recent.hook.js'

const DEFAULT_MAX_COUNT = 30
const DEFAULT_MINE_LIMIT = 6

export interface UseRecentSplitProps {
  /** Forwarded to `useRecent` as the server-side fetch ceiling. */
  maxCount?: number
  /** Cap on the `mine` slice. The Continue-working section consumes at most this many. */
  mineLimit?: number
}

export interface RecentSplitResult {
  mine: Persisted<Entity>[]
  theirs: Persisted<Entity>[]
  isLoading: boolean
}

/**
 * Split the existing server-side recent feed into `mine` (entities I last-edited)
 * and `theirs` (everything else, including legacy entries without `lastEditedBy`).
 * Pure derivation — no new server query. v1 trades tail correctness for simplicity;
 * pinning covers long-tail items.
 */
export const useRecentSplit = (props: UseRecentSplitProps = {}): RecentSplitResult => {
  const { maxCount = DEFAULT_MAX_COUNT, mineLimit = DEFAULT_MINE_LIMIT } = props
  const me = useCurrentUser()
  const { searchResults, isLoading } = useRecent({ maxCount })
  const mine = searchResults.filter(e => e.lastEditedBy === me.userId).slice(0, mineLimit)
  const theirs = searchResults.filter(e => e.lastEditedBy !== me.userId)
  return { mine, theirs, isLoading }
}
